import { z } from 'zod';
import { isoDateTime, looseBoolean, wireDecimal } from './common';
// A tab IS an order, so the round and payment shapes are the checkout ones
// rather than parallel copies that could drift apart from them.
import { wirePaymentSummarySchema, wireRoundSchema } from './checkout';

/**
 * Restaurant floor + tab domain (V1 fast-follow).
 *
 * Endpoint evidence: pos-api `app/Http/Controllers/Api/POS/TableController.php`
 * and the three tab mutations on `Api/POS/OrderController.php`. Every route
 * below runs `pos.terminal`; the floor reads and tab service run
 * `pos.role:tables.serve`, settlement runs `pos.role:sell`.
 *
 * A table has no status column upstream. Its state is DERIVED from the open
 * order sitting on it (`tableState` below), so the floor map can never
 * disagree with the tab it is drawing.
 */

/* ------------------------------------------------------------------ wire */

/**
 * The tab summary embedded in each floor row. Identifiers, counts and totals
 * only — the floor endpoint deliberately returns no guest name or phone.
 */
export const wireOpenTabSummarySchema = z.looseObject({
  id: z.number(),
  order_number: z.string().nullish(),
  status: z.string(),
  sub_total: wireDecimal.nullish(),
  total_amount: wireDecimal.nullish(),
  items_count: z.number().nullish(),
  // Guests seated. Read out of orders.customer_metadata upstream — there is no
  // covers column — and null whenever the tab was opened without one.
  covers: z.number().nullish(),
  bill_requested_at: isoDateTime.nullish(),
  opened_by_user_id: z.number().nullish(),
  opened_at: isoDateTime.nullish(),
});

export const wireTableSchema = z.looseObject({
  id: z.number(),
  branch_id: z.number(),
  label: z.string(),
  // The floor the table stands on, by NAME as well as id: a waiter reads
  // "Terrace", and the service floor has no floor list of its own to look an
  // id up in. Nullish so a tenant whose floors migration has not run still
  // parses — those tables fall back to `section`.
  floor_id: z.number().nullish(),
  floor_name: z.string().nullish(),
  section: z.string().nullish(),
  seats: z.number().nullish(),
  sort_order: z.number().nullish(),
  is_active: looseBoolean,
  open_order: wireOpenTabSummarySchema.nullish(),
});

export const wireFloorSchema = z.looseObject({
  data: z.looseObject({
    tables: z.array(wireTableSchema),
  }),
});

export const wireTabItemSchema = z.looseObject({
  id: z.number(),
  product_id: z.number().nullish(),
  parent_order_item_id: z.number().nullish(),
  name: z.string().nullish(),
  quantity: z.number(),
  unit_price: wireDecimal.nullish(),
  total_price: wireDecimal.nullish(),
  attributes: z.unknown().nullish(),
  created_at: isoDateTime.nullish(),
});

export const wireTabDetailSchema = z.looseObject({
  data: z.looseObject({
    table_id: z.number(),
    order: z
      .looseObject({
        id: z.number(),
        order_number: z.string().nullish(),
        status: z.string(),
        order_type: z.string().nullish(),
        sub_total: wireDecimal.nullish(),
        delivery_charges: wireDecimal.nullish(),
        discount_amount: wireDecimal.nullish(),
        total_amount: wireDecimal.nullish(),
        notes: z.string().nullish(),
        covers: z.number().nullish(),
        bill_requested_at: isoDateTime.nullish(),
        opened_by_user_id: z.number().nullish(),
        opened_at: isoDateTime.nullish(),
        items: z.array(wireTabItemSchema),
      })
      .nullish(),
  }),
});

/**
 * Mutation envelopes. Only `order.id` is read: like `ordersApi.updateStatus`,
 * these adapters never fabricate the resulting state from a write response —
 * callers re-fetch. Parsing the full Eloquent order here would couple the
 * console to a serialization it does not otherwise depend on.
 */
/**
 * Response to a tab mutation.
 *
 * `order` stays deliberately loose — most callers read only its id and
 * re-fetch, which is what keeps two devices on one floor consistent. Settlement
 * is the exception: its response IS the receipt, so it is parsed in full there.
 *
 * `round` is the batch just accepted, and it is the only thing that can render
 * a kitchen ticket for the NEW items rather than for the whole tab again.
 */
export const wireTabMutationSchema = z.looseObject({
  success: z.boolean().nullish(),
  order: z.looseObject({ id: z.number() }).nullish(),
  round: wireRoundSchema.nullish(),
  payment: wirePaymentSummarySchema.nullish(),
});

export type WireTable = z.infer<typeof wireTableSchema>;
export type WireTabItem = z.infer<typeof wireTabItemSchema>;

/* ---------------------------------------------------------------- domain */

/** Derived floor state. There is no such column upstream. */
export type TableState = 'free' | 'occupied' | 'bill_requested';

export interface OpenTabSummary {
  orderId: number;
  orderNumber: string | null;
  status: string;
  subTotal: string;
  totalAmount: string;
  itemsCount: number;
  /** Guests seated. Null when the tab was opened without a count. */
  covers: number | null;
  billRequestedAt: string | null;
  openedByUserId: number | null;
  openedAt: string | null;
}

export interface RestaurantTable {
  id: number;
  branchId: number;
  label: string;
  floorId: number | null;
  floorName: string | null;
  section: string | null;
  seats: number | null;
  sortOrder: number;
  isActive: boolean;
  openTab: OpenTabSummary | null;
}

export interface TabItem {
  id: number;
  productId: number | null;
  parentItemId: number | null;
  name: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  createdAt: string | null;
}

export interface TabDetail {
  tableId: number;
  orderId: number;
  orderNumber: string | null;
  status: string;
  subTotal: string;
  discountAmount: string;
  totalAmount: string;
  notes: string | null;
  /** Guests seated. Null when the tab was opened without a count. */
  covers: number | null;
  billRequestedAt: string | null;
  openedByUserId: number | null;
  openedAt: string | null;
  items: TabItem[];
}

/* -------------------------------------------------------------- adapters */

export function normalizeOpenTab(wire: z.infer<typeof wireOpenTabSummarySchema>): OpenTabSummary {
  return {
    orderId: wire.id,
    orderNumber: wire.order_number ?? null,
    status: wire.status,
    subTotal: wire.sub_total ?? '0',
    totalAmount: wire.total_amount ?? '0',
    itemsCount: wire.items_count ?? 0,
    covers: wire.covers ?? null,
    billRequestedAt: wire.bill_requested_at ?? null,
    openedByUserId: wire.opened_by_user_id ?? null,
    openedAt: wire.opened_at ?? null,
  };
}

export function normalizeTable(wire: WireTable): RestaurantTable {
  return {
    id: wire.id,
    branchId: wire.branch_id,
    label: wire.label,
    floorId: wire.floor_id ?? null,
    floorName: wire.floor_name ?? null,
    section: wire.section ?? null,
    seats: wire.seats ?? null,
    sortOrder: wire.sort_order ?? 0,
    isActive: wire.is_active,
    openTab: wire.open_order ? normalizeOpenTab(wire.open_order) : null,
  };
}

export function normalizeTabItem(wire: WireTabItem): TabItem {
  return {
    id: wire.id,
    productId: wire.product_id ?? null,
    parentItemId: wire.parent_order_item_id ?? null,
    // A deleted product leaves the line with no name; the quantity and price
    // are still real, so the line is shown rather than dropped.
    name: wire.name ?? 'Removed item',
    quantity: wire.quantity,
    unitPrice: wire.unit_price ?? '0',
    totalPrice: wire.total_price ?? '0',
    createdAt: wire.created_at ?? null,
  };
}

/* --------------------------------------------------------------- helpers */

/**
 * A table's state, derived rather than stored.
 *
 * `bill_requested` outranks `occupied` because it is the state that needs
 * someone to walk over: it is what the floor map sorts and colours by.
 */
export function tableState(table: RestaurantTable): TableState {
  if (!table.openTab) return 'free';
  return table.openTab.billRequestedAt ? 'bill_requested' : 'occupied';
}

/** Sections in floor order, with their tables. Untitled tables group last. */
export function groupTablesBySection(
  tables: RestaurantTable[],
): { section: string; tables: RestaurantTable[] }[] {
  const groups = new Map<string, RestaurantTable[]>();

  for (const table of tables) {
    const key = table.section?.trim() || 'Floor';
    const bucket = groups.get(key);
    if (bucket) bucket.push(table);
    else groups.set(key, [table]);
  }

  return [...groups.entries()].map(([section, list]) => ({
    section,
    tables: [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
  }));
}

/**
 * The floor grouped by STATE, in the order a waiter scans it.
 *
 * Sections describe the room; state describes the work. A waiter walking in
 * does not ask "what is on the terrace", they ask "who is waiting for me" — and
 * with the floor grouped by section that answer was scattered across every
 * heading, so a bill requested on the terrace and one in the hall were two
 * separate hunts.
 *
 * `bill_requested` leads because it is the only state where somebody is
 * actively waiting; `free` sorts last because an empty table needs nothing. The
 * table's section still rides on the row, so nothing about the room is lost.
 *
 * Empty groups are dropped rather than rendered as a heading with nothing under
 * it — a floor with no free tables should say so by having no Free group, not
 * by drawing an empty one.
 */
const STATE_ORDER: TableState[] = ['bill_requested', 'occupied', 'free'];

export const TABLE_GROUP_LABELS: Record<TableState, string> = {
  bill_requested: 'Bill requested',
  occupied: 'Occupied',
  free: 'Free',
};

export function groupTablesByState(
  tables: RestaurantTable[],
): { state: TableState; label: string; tables: RestaurantTable[] }[] {
  const groups = new Map<TableState, RestaurantTable[]>();

  for (const table of tables) {
    const state = tableState(table);
    const bucket = groups.get(state);
    if (bucket) bucket.push(table);
    else groups.set(state, [table]);
  }

  return STATE_ORDER.filter((state) => groups.has(state)).map((state) => ({
    state,
    label: TABLE_GROUP_LABELS[state],
    tables: [...(groups.get(state) ?? [])].sort(
      (a, b) =>
        // The room keeps its tables together inside a state, then the floor's
        // own order, then the label — so a table never moves for two reasons at
        // once. Floor name first, `section` only for rows that have no floor.
        (a.floorName ?? a.section ?? '').localeCompare(b.floorName ?? b.section ?? '') ||
        a.sortOrder - b.sortOrder ||
        a.label.localeCompare(b.label),
    ),
  }));
}

/** The area a table is in, by whichever of the two the tenant actually has. */
export function tableAreaName(table: RestaurantTable): string | null {
  return table.floorName?.trim() || table.section?.trim() || null;
}

/** The heading a table with no floor at all sits under. */
export const NO_FLOOR_LABEL = 'No floor';

export interface FloorStateGroup {
  state: TableState;
  label: string;
  tables: RestaurantTable[];
}

export interface FloorSection {
  /** Null for the group holding tables that are on no floor. */
  floorId: number | null;
  heading: string;
  tables: RestaurantTable[];
  groups: FloorStateGroup[];
}

/**
 * The floor as a waiter walks it: BY ROOM first, by state within the room.
 *
 * Both questions are real and neither replaces the other. "Who is waiting for
 * me" is why the state grouping exists; "which room am I walking to" is why it
 * is nested under the floor rather than the other way round — a waiter working
 * the terrace should not have to read past the hall's tables to find theirs.
 *
 * ONE FLOOR IS NOT A GROUPING. A branch whose tables all sit in one room gets a
 * single heading that says nothing, so the caller is given `floorId` and can
 * fall back to the flat state grouping when there is only one section. That
 * decision is left to the screen because it is a layout question.
 *
 * Floors are ordered by the table order the server already sorted by, so the
 * rooms appear in the order the floor plan lists them rather than
 * alphabetically. Tables with no floor group last: they are a setup gap, not a
 * room, and burying them would hide it.
 */
export function groupTablesByFloor(tables: RestaurantTable[]): FloorSection[] {
  const sections = new Map<string, FloorSection>();

  for (const table of tables) {
    const area = tableAreaName(table);
    // Keyed by id where there is one, by name for a legacy `section` string, so
    // two floors that happen to share a name are not merged into one room.
    const key = table.floorId != null ? `id:${table.floorId}` : area ? `name:${area}` : 'none';

    let section = sections.get(key);
    if (!section) {
      section = {
        floorId: table.floorId ?? null,
        heading: area ?? NO_FLOOR_LABEL,
        tables: [],
        groups: [],
      };
      sections.set(key, section);
    }
    section.tables.push(table);
  }

  const ordered = [...sections.values()].sort((a, b) => {
    if (a.heading === NO_FLOOR_LABEL) return 1;
    if (b.heading === NO_FLOOR_LABEL) return -1;
    return 0;
  });

  for (const section of ordered) {
    section.groups = groupTablesByState(section.tables);
  }

  return ordered;
}

/** Minutes a tab has been open, for the floor map's dwell indicator. */
export function tabAgeMinutes(tab: OpenTabSummary, now: number = Date.now()): number | null {
  if (!tab.openedAt) return null;
  const opened = Date.parse(tab.openedAt);
  if (Number.isNaN(opened)) return null;
  return Math.max(0, Math.floor((now - opened) / 60_000));
}

/* ----------------------------------------------------- floor plan (office) */

/**
 * Floor-plan administration — the office side of the same tables.
 *
 * Endpoint evidence: pos-api `routes/api.php` — `GET /tables/manage`,
 * `POST /tables`, `PUT /tables/{id}`, `POST /tables/reorder`, all
 * `pos.role:tables.manage`. There is no DELETE route.
 *
 * This is a SEPARATE shape from `RestaurantTable` above rather than a
 * superset, because the two views answer different questions. The service
 * floor asks "who is sitting here and what do they owe", so it carries the tab
 * and its covers. The office asks "may I retire this table", so it carries only
 * whether a tab is open right now. Collapsing them would put an unused tab
 * summary on every setup row.
 *
 * There is no delete: a table leaves service by being retired. Orders reference
 * tables with `nullOnDelete` upstream, so deleting one would silently detach
 * every order it ever served.
 */
export const wireManagedTableSchema = z.looseObject({
  id: z.number(),
  branch_id: z.number(),
  label: z.string(),
  // The floor this table stands on, or null for one not yet placed. `section`
  // is kept beside it and still read as the fallback grouping — a tenant whose
  // floors migration has not run yet has strings and no ids.
  floor_id: z.number().nullish(),
  section: z.string().nullish(),
  seats: z.number().nullish(),
  sort_order: z.number().nullish(),
  is_active: looseBoolean,
  // Absent from the write responses: no write here seats or clears a table, so
  // the server does not recompute it for a single row.
  has_open_tab: looseBoolean.nullish(),
});

export const wireManagedFloorSchema = z.looseObject({
  data: z.looseObject({
    tables: z.array(wireManagedTableSchema),
  }),
});

export const wireManagedTableMutationSchema = z.looseObject({
  success: z.boolean().nullish(),
  data: z.looseObject({ table: wireManagedTableSchema }).nullish(),
});

export type WireManagedTable = z.infer<typeof wireManagedTableSchema>;

export interface ManagedTable {
  id: number;
  branchId: number;
  label: string;
  floorId: number | null;
  section: string | null;
  seats: number | null;
  sortOrder: number;
  isActive: boolean;
  /** A tab is open on this table right now, so it cannot be retired. */
  hasOpenTab: boolean;
}

/**
 * The editable half of a table. Every field is optional because the server
 * treats each one as `sometimes`: a screen toggling availability sends only
 * `isActive` and cannot blank a section by omitting it.
 */
export interface TableDraft {
  label?: string;
  /** `null` moves the table off its floor; absent leaves it where it stands. */
  floorId?: number | null;
  section?: string | null;
  seats?: number | null;
  sortOrder?: number;
  isActive?: boolean;
}

export function normalizeManagedTable(wire: WireManagedTable): ManagedTable {
  return {
    id: wire.id,
    branchId: wire.branch_id,
    label: wire.label,
    floorId: wire.floor_id ?? null,
    section: wire.section ?? null,
    seats: wire.seats ?? null,
    sortOrder: wire.sort_order ?? 0,
    isActive: wire.is_active,
    hasOpenTab: wire.has_open_tab ?? false,
  };
}

/** Floor-plan sections in display order. Untitled tables group last. */
export function groupManagedBySection(
  tables: ManagedTable[],
): { section: string; tables: ManagedTable[] }[] {
  const groups = new Map<string, ManagedTable[]>();

  for (const table of tables) {
    const key = table.section?.trim() || 'Unsectioned';
    const bucket = groups.get(key);
    if (bucket) bucket.push(table);
    else groups.set(key, [table]);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => {
      // "Unsectioned" is a fallback label, not a section someone named, so it
      // sorts last however the real sections are named.
      if (a === 'Unsectioned') return 1;
      if (b === 'Unsectioned') return -1;
      return a.localeCompare(b);
    })
    .map(([section, list]) => ({
      section,
      tables: [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    }));
}
